import type {QueryClient, UseQueryOptions} from '@tanstack/react-query'
import {keepPreviousData, queryOptions, useQuery, useQueryClient} from '@tanstack/react-query'

import type {SupportedLocales} from '@/lib/locale/locale'
import {useLocale} from '@/lib/locale/locale'
import type {SearchResult} from '@/models/home_deprecated/searchResultModel'
import {getSearchResult} from '@/services/homeService'
import {NO_REFETCH_OPTIONS} from '@/utils/query/constants'

export function getSearchResultQueryKey(params: {q: string; locale: SupportedLocales}) {
  return ['searchResult', params.q, params.locale] as const
}

function getSearchResultQueryOptions<TData = SearchResult | false, TError = Error>(
  params: {q: string; locale: SupportedLocales; queryClient: QueryClient},
  options?: Omit<UseQueryOptions<SearchResult | false, TError, TData>, 'queryKey' | 'queryFn'>,
) {
  const queryKey = getSearchResultQueryKey({
    q: params.q,
    locale: params.locale,
  })

  return queryOptions({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- use the same query key to preserve old behavior
    queryKey,
    queryFn: async ({signal, queryKey}) => {
      const result = await getSearchResult({
        q: params.q,
        locale: params.locale,
        signal,
        queryKey,
        queryClient: params.queryClient,
      })

      return result
    },
    placeholderData: keepPreviousData,
    ...NO_REFETCH_OPTIONS,
    ...options,
  })
}

export default function useSearchResult<TData = SearchResult | false, TError = Error>(
  params: {q: string},
  options?: Omit<UseQueryOptions<SearchResult | false, TError, TData>, 'queryKey' | 'queryFn'>,
) {
  const [locale] = useLocale()
  const queryClient = useQueryClient()

  const queryResult = useQuery(
    getSearchResultQueryOptions<TData, TError>(
      {
        q: params.q,
        locale,
        queryClient,
      },
      options,
    ),
  )

  return queryResult
}
